// Public route metadata only. Paid tasks and guidance come from the entitled guide item.
import { practiceView } from '../practiceActivities/model.js';

export const ARTICLE_PACK_ID = 'edexcel-4ea1-transactional-writing';
export const ARTICLE_ROUTE_ID = '__article_pilot__';
export const ARTICLE_GUIDE_TITLE = 'Article pilot — Learn, Practise, Review';
export const ARTICLE_GUIDE_UNIT = 'Form Mastery — Magazine and Newspaper Article';
export const ARTICLE_MODEL_ID = 'item_d424d8ef181955f7a37f977b';
export const ARTICLE_PARAGRAPH_GUIDE_ID = 'item_1f5b2baef09a5720f3d15633';
export const ARTICLE_PROMPT_IDS = Object.freeze({
    publicSpaces: ARTICLE_MODEL_ID,
    boredom: 'item_59239256e06bb58e534eec7b',
    partTime: 'item_0bf036ee53f7441817912e8c',
    neighbourhood: 'item_95c8d0c156f7bb2757e83b67',
});
export const ARTICLE_REFERENCE_IDS = Object.freeze({
    architecture: 'item_f0b1d82830119dca278f763d',
    paragraphGuide: ARTICLE_PARAGRAPH_GUIDE_ID,
    persuasion: 'item_6a39b78c241e21d0c7e813c2',
    preciseJudgements: 'item_0a3bb83fe6d55a27cb59730e',
    concessionContrast: 'item_89e3c6a6dd44527eab88feb5',
});
export const ARTICLE_STEPS = Object.freeze([
    {
        "id": "article_pilot_1",
        "phase": "Learn",
        "title": "Read the task before planning",
        "minutes": 4,
        "learningGoal": "Identify the reader, purpose and position the article needs.",
        "deliverable": "Four task notes and a one-sentence position.",
        "links": [
            {
                "itemId": "item_d424d8ef181955f7a37f977b",
                "label": "Read the original question"
            },
            {
                "itemId": "item_f0b1d82830119dca278f763d",
                "label": "Article Architecture"
            }
        ]
    },
    {
        "id": "article_pilot_2",
        "phase": "Learn",
        "title": "Compare two openings",
        "minutes": 6,
        "learningGoal": "Explain how an opening suits a reader and establishes a position.",
        "deliverable": "An explained choice and your own opening.",
        "links": [
            {
                "itemId": "item_1f5b2baef09a5720f3d15633",
                "label": "Read the paragraph guide"
            }
        ]
    },
    {
        "id": "article_pilot_3",
        "phase": "Learn",
        "title": "Follow the six-paragraph argument",
        "minutes": 12,
        "learningGoal": "Trace how topic sentences, development and paragraph order support the thesis.",
        "deliverable": "Four paragraph job labels and one explained connection.",
        "links": [
            {
                "itemId": "item_d424d8ef181955f7a37f977b",
                "label": "Original model source"
            },
            {
                "itemId": "item_1f5b2baef09a5720f3d15633",
                "label": "Six-paragraph guide"
            }
        ]
    },
    {
        "id": "article_pilot_4",
        "phase": "Practise",
        "title": "Develop a reason into a paragraph",
        "minutes": 12,
        "learningGoal": "Add an example, explanation and limitation that support one topic sentence.",
        "deliverable": "Original paragraph; revision; one sentence explaining the improvement.",
        "links": [
            {
                "itemId": "item_6a39b78c241e21d0c7e813c2",
                "label": "Persuasion in Practice"
            }
        ]
    },
    {
        "id": "article_pilot_5",
        "phase": "Practise",
        "title": "Choose language for meaning and control",
        "minutes": 8,
        "learningGoal": "Make a judgement precise, address the reader respectfully and repair a sentence boundary.",
        "deliverable": "Three revised sentences and three brief reasons.",
        "links": [
            {
                "itemId": "item_0a3bb83fe6d55a27cb59730e",
                "label": "Precise Judgements"
            },
            {
                "itemId": "item_89e3c6a6dd44527eab88feb5",
                "label": "Concession and Contrast"
            }
        ]
    },
    {
        "id": "article_pilot_6",
        "phase": "Practise",
        "title": "Transfer to a new topic: boredom",
        "minutes": 12,
        "learningGoal": "Develop the same writing skills in a different topic and for a student readership.",
        "deliverable": "A thesis; original and revised paragraph; one reason for a choice.",
        "links": [
            {
                "itemId": "item_59239256e06bb58e534eec7b",
                "label": "Read the Boredom question"
            }
        ]
    },
    {
        "id": "article_pilot_7",
        "phase": "Practise",
        "title": "Choose one article task: 45 minutes",
        "minutes": 45,
        "learningGoal": "Select a task, plan and write one complete article independently.",
        "deliverable": "One article and a record of the chosen question and support used.",
        "links": [
            {
                "itemId": "item_0bf036ee53f7441817912e8c",
                "label": "Question A — Part-time work"
            },
            {
                "itemId": "item_95c8d0c156f7bb2757e83b67",
                "label": "Question B — Neighbourhood events"
            }
        ]
    },
    {
        "id": "article_pilot_8",
        "phase": "Review",
        "title": "Use evidence to choose your next target",
        "minutes": 10,
        "learningGoal": "Identify a specific writing improvement and test it in new work.",
        "deliverable": "One or two evidence-based targets and a revised section.",
        "links": [
            {
                "itemId": "item_1f5b2baef09a5720f3d15633",
                "label": "Revisit the paragraph guide"
            },
            {
                "itemId": "item_0a3bb83fe6d55a27cb59730e",
                "label": "Choose a language target"
            }
        ]
    }
].map(step => Object.freeze({ ...step, links: Object.freeze(step.links.map(Object.freeze)) })));

export function isArticlePilotGuide(item) {
    const content = item?.content, parts = content?.blocks?.parts;
    return item?.type === 'lit_section' && content?.sectionKind === 'technique'
        && content.title === ARTICLE_GUIDE_TITLE && content.blocks?.unit === ARTICLE_GUIDE_UNIT
        && Array.isArray(parts) && parts.length === ARTICLE_STEPS.length
        && parts.every((part, index) => part?.id === ARTICLE_STEPS[index].id);
}

// The caller supplies paidItems from the existing entitled subscription. A title
// alone, an unknown item or ambiguous duplicate never enables paid activities.
export function findArticlePilotGuide({ entitled, paidItems } = {}) {
    if (!entitled || !Array.isArray(paidItems)) return null;
    const matches = paidItems.filter(isArticlePilotGuide);
    return matches.length === 1 ? matches[0] : null;
}

export function articlePilotStepContent({ entitled, guide, stepId } = {}) {
    if (!entitled || !isArticlePilotGuide(guide)) return null;
    const index = ARTICLE_STEPS.findIndex(step => step.id === stepId);
    if (index < 0) return null;
    const view = practiceView(guide.content);
    if (!view) return null;
    const part = guide.content.blocks.parts[index];
    const activity = view.activities.find(row => row.partId === stepId);
    const task = index < 6 ? view.text(activity?.instructionRef) : part.body;
    const guidance = view.text(index < 6 ? activity?.answerRef : stepId + '_guidance');
    const hints = view.text(stepId + '_hints');
    const openingB = index === 1 ? view.text('article_pilot_opening_b') : '';
    if (!task?.trim() || !guidance?.trim() || !hints?.trim() || (index === 1 && !openingB?.trim())) return null;
    return { task, guidance, hints, openingB };
}
